import { inject, Service } from "@angular/core";

import { AdaaHelper, isDsV3 } from "../../core/utils";
import { Constants } from "../constants/constants";
import { SearchFields } from "../models";
import { MainService } from "./main-service";

@Service()
export class DataTableDataService {
  private readonly _mainService = inject(MainService);

  private _getUrlSuffix(paramsString?: string, limit?: number, offset?: number, useSlash: boolean = false) {
    let url = "?";
    const qs = new URLSearchParams();

    if (AdaaHelper.isDefinedAndNotEmpty(paramsString)) url = useSlash ? `/${paramsString}?` : `?${paramsString}&`;

    if (AdaaHelper.isDefined(limit)) qs.append("limit", limit.toString());
    if (AdaaHelper.isDefined(offset)) qs.append("offset", offset.toString());

    return `${url}${qs}`;
  }

  public getAllV2(
    limit?: number,
    offset?: number,
    prefix: string = "item",
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `${prefix}/getallv2${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getAllV2Small(
    limit?: number,
    offset?: number,
    prefix: string = "item",
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `${prefix}/getAllv2${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getAll(
    limit?: number,
    offset?: number,
    prefix: string = "item",
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `${prefix}/getall${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getAllCamelCase(
    limit?: number,
    offset?: number,
    prefix: string = "item",
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `${prefix}/getAll${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getSubGovKpisByPeriod(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `kpi/getSubGovKpisByPeriod${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getInitiativesCrossCycle(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `initiative/bySubGov${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getKpiScoresByAggregationType(
    limit?: number,
    offset?: number,
    prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `${prefix}/getKpiScoresByAggregationType${url}`;

    return this._mainService.sendRequest({
      url,
      method: "POST",
      data: searchFields,
      loader,
    });
  }

  public getProjectScoresByAggregationType(
    limit?: number,
    offset?: number,
    prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `${prefix}/getProjectScoresByAggregationType${url}`;

    return this._mainService.sendRequest({
      url,
      method: "POST",
      data: searchFields,
      loader,
    });
  }

  public getNationalProjectKpis(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `kpi/getNationalProjectKpisByPeriod${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getInternationalProjectKpisByPeriod(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `kpi/getInternationalProjectKpisByPeriod${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getNationalProjectMilestonesKpis(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `kpi/getProjectMilestoneKpisByPeriod${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getUsersList(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `users/getall${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getOtherEntitiesList(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `other-entity/getGridList${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getLookupList(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `prop/getAllManage${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getLookupContentList(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset, true);

    url = `prop/getallbyproptypeid${url}&itemType=67`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getAllPermissionsByUserId(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset, true);

    url = `users/getAllPermissionsByUserId${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public queryWorkflowMonitoringList(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    _loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `wftrigger/getAllMonitoring${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      data: searchFields,
    });
  }

  public getUsersGroupsList(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `usergroups/getall${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public queryAuditTrail(
    limit?: number,
    offset?: number,
    _prefix: string = "item",
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `audittrail/getAll${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getEntityReportsList(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset, true);

    url = `file/getAllByItemTypeId${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getSynchronizationHistory(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `khadamati/getSyncHistory${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getSynchronizationHistoryDetail(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset, true);

    url = `khadamati/getSyncHistoryDetail${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getAllNationalStrategies(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `govdirection/getAllNationalStrategies${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getMandatoryList(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    const statusParams = [Constants.OBJECT_STATUS.IN_ACTIVE, Constants.OBJECT_STATUS.DRAFT]
      .map((s) => `ignoredStatus=${s}`)
      .join("&");

    url = AdaaHelper.isDefinedAndNotEmpty(url) ? `${url}&${statusParams}` : `?${statusParams}`;
    url = `validator/getallbytype/66${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getAllSharedAndLocal(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `libmetric/getAllSharedAndLocal${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getAllMonitoring(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `libmetric/getAllMonitoring${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getAllMonitoringV2(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `libmetric/getAllMonitoringV2${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getAuditList(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `dataentry/getauditkpis${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getGovScoresPillars(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `govScoresPillars/getAllPillarsByEntity${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getAgreements(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `${isDsV3() ? "project" : "national-project"}/getAgreements${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getAllV3(
    limit?: number,
    offset?: number,
    prefix: string = "item",
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `${prefix}/getallv3${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getInitiativeByIdAndPriorityV2(
    limit?: number,
    offset?: number,
    _prefix: string = "item",
    paramsString?: string,
    _loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `initiative/getinitiativebyidandpriority_v2${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      data: searchFields,
    });
  }

  public getSyncLogs(
    limit?: number,
    offset?: number,
    prefix: string = "",
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `${prefix}/getSyncLogs${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getSyncDetails(
    limit?: number,
    offset?: number,
    prefix: string = "",
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset, true);

    url = `${prefix}/getSyncLogs${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getDimensionsStructureSyncLogsDetails(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset, true);

    url = `libmetric/getStructureSyncLogs${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getDimensionsValueSyncLogsDetails(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset, true);

    url = `libmetric/getValueSyncLogs${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getFAQSCategoryHelp(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `faqs/categoryGetall${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getFAQS(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `faqs/getAllWithUnpublish${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getTooltips(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `validator/getallbytype/70${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public forecastedValuesMetrics(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `forecasted-values/getMetrics${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getHelpPaths(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `help/getPaths${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getInternationalProjectContacts(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset, true);

    url = `international-project/getAllContacts${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public dataEntryAllActivities(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset, false);

    url = `dataentry/getAllActivities${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getConfiguredSchedulers(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset, false);

    url = `entity-data-sync/getConfiguredSchedulers${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getFavorites(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset, false);
    const userId = AdaaHelper.getLocalStorage(Constants.localStorageKeys.user, { type: "prop", property: "id" });

    url = `favorite/userv2/${userId}${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getEmployeePulseLogs(
    limit?: number,
    offset?: number,
    _prefix?: string,
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `employee-pulse/logs${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getGovTracks(
    limit?: number,
    offset?: number,
    prefix: string = "",
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `${prefix}/getAllTracks${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getGovMilestones(
    limit?: number,
    offset?: number,
    prefix: string = "",
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `${prefix}/getAllMilestones${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }

  public getGovTasks(
    limit?: number,
    offset?: number,
    prefix: string = "",
    paramsString?: string,
    loader: boolean = true,
    searchFields?: SearchFields
  ) {
    let url = this._getUrlSuffix(paramsString, limit, offset);

    url = `${prefix}/getAllTasks${url}`;

    return this._mainService.sendRequest({
      method: "POST",
      url,
      loader,
      data: searchFields,
    });
  }
}
